import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { MatchdayPlanning, PlanningSegment, VEHICLE_CONFIGS, ANCHOR_CONFIGS, StaffRole } from '../types/matchdayPlanning';

// Brand colors
const PRIMARY: [number, number, number] = [30, 58, 95];    // Dark navy
const ACCENT: [number, number, number] = [41, 128, 185];   // Blue
const LIGHT_BG: [number, number, number] = [245, 247, 250];
const BORDER: [number, number, number] = [200, 210, 220];

/**
 * Generate a professional PDF document for a matchday planning (player-facing)
 */
export const generatePlanningPDF = (planning: MatchdayPlanning): void => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  let y = 0;

  const checkPageBreak = (space: number) => {
    if (y + space > pageHeight - 20) {
      doc.addPage();
      y = margin;
      return true;
    }
    return false;
  };

  // ===== HEADER BAR =====
  doc.setFillColor(...PRIMARY);
  doc.rect(0, 0, pageWidth, 42, 'F');

  // Match title (Home vs Away) or planning name
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');

  let title = planning.name;
  if (planning.match?.homeClub && planning.match?.awayClub) {
    title = `${planning.match.homeClub.name}  vs  ${planning.match.awayClub.name}`;
  }
  doc.text(title, pageWidth / 2, 18, { align: 'center' });

  // Subtitle: date + kickoff
  const dateStr = new Date(planning.date).toLocaleDateString('de-DE', {
    weekday: 'long', day: '2-digit', month: 'long', year: 'numeric'
  });
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(`${dateStr}  |  Anstoß: ${planning.kickoffTime} Uhr`, pageWidth / 2, 30, { align: 'center' });

  // Optional: Stadium + Matchday
  let subLine = '';
  if (planning.match?.homeClub?.stadium) subLine += planning.match.homeClub.stadium;
  if (planning.match?.homeClub?.city) subLine += subLine ? `, ${planning.match.homeClub.city}` : planning.match.homeClub.city;
  if (planning.match?.matchday) subLine += subLine ? `  |  ${planning.match.matchday.number}. Spieltag` : `${planning.match.matchday.number}. Spieltag`;
  if (planning.localMatchday) subLine = planning.localMatchday.name + (planning.localMatchday.location ? ` – ${planning.localMatchday.location}` : '');
  if (subLine) {
    doc.setFontSize(9);
    doc.text(subLine, pageWidth / 2, 38, { align: 'center' });
  }

  y = 52;

  // ===== TIMELINE TABLE =====
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.text('Reiseplan', margin, y);
  y += 7;

  const sortedSegments = planning.segments
    ? [...planning.segments].sort((a, b) => a.sequenceOrder - b.sequenceOrder)
    : [];

  // Build table rows (no gap rows)
  const tableBody: Array<{ cells: string[]; isGap: boolean }> = [];
  sortedSegments.forEach((seg) => {
    const dateLabel = seg.scheduledDate
      ? new Date(seg.scheduledDate).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' })
      : '';
    const time = seg.scheduledTime?.substring(0, 5) || '';
    const location = seg.customLocation || seg.club?.name || seg.location;
    const anchor = ANCHOR_CONFIGS[seg.anchorType]?.name || seg.anchorType;
    const hideVehicle = seg.anchorType === 'stopover' || seg.anchorType === 'main';
    const vehicle = hideVehicle ? '' : (VEHICLE_CONFIGS[seg.vehicleType]?.name || seg.vehicleType);
    const driver = hideVehicle ? '' : (seg.vehicleAssignment?.traveler?.fullName || seg.vehicleAssignment?.partnerDriver?.fullName || '');
    const desc = seg.description || '';

    tableBody.push({
      cells: [dateLabel, time, `${anchor}: ${location}`, vehicle, driver, desc],
      isGap: false
    });
  });

  autoTable(doc, {
    startY: y,
    head: [['Datum', 'Zeit', 'Station', 'Fahrzeug', 'Fahrer', 'Hinweis']],
    body: tableBody.map(r => r.cells),
    theme: 'grid',
    headStyles: {
      fillColor: PRIMARY,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 9,
      cellPadding: 4
    },
    styles: { fontSize: 9, cellPadding: 3, lineColor: BORDER, lineWidth: 0.3 },
    columnStyles: {
      0: { cellWidth: 24 },
      1: { cellWidth: 16 },
      2: { cellWidth: 52 },
      3: { cellWidth: 24 },
      4: { cellWidth: 30 },
      5: { cellWidth: 'auto' }
    },
    margin: { left: margin, right: margin },
    didParseCell: (data: any) => {
      const row = data.row.index;
      if (tableBody[row]?.isGap) {
        data.cell.styles.fillColor = [235, 240, 248];
        data.cell.styles.textColor = [100, 120, 150];
        data.cell.styles.fontStyle = 'italic';
        data.cell.styles.fontSize = 8;
        data.cell.styles.cellPadding = 1.5;
      }
      // Highlight main event row
      if (!tableBody[row]?.isGap) {
        const segIdx = tableBody.slice(0, row + 1).filter(r => !r.isGap).length - 1;
        if (sortedSegments[segIdx]?.isMainEvent) {
          data.cell.styles.fillColor = [255, 243, 224];
          data.cell.styles.fontStyle = 'bold';
        }
      }
    },
    alternateRowStyles: { fillColor: LIGHT_BG }
  });

  y = (doc as any).lastAutoTable.finalY + 12;

  // ===== VEHICLE ASSIGNMENTS =====
  const vehicles = planning.vehicleAssignments;
  if (vehicles && vehicles.length > 0) {
    checkPageBreak(30);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Fahrzeuge', margin, y);
    y += 7;

    const vRows = vehicles.map(va => {
      const type = VEHICLE_CONFIGS[va.vehicleType]?.name || va.vehicleType;
      const name = va.vehicle?.name || va.transportation?.name || '-';
      const plate = va.vehicle?.licensePlate || '';
      const cap = va.vehicle?.capacity ? `${va.vehicle.capacity} Plätze` : '';
      const driver = va.traveler?.fullName || va.partnerDriver?.fullName || '-';
      const partner = va.partner?.name || '';
      return [type, name, plate, cap, driver, partner];
    });

    autoTable(doc, {
      startY: y,
      head: [['Typ', 'Fahrzeug', 'Kennzeichen', 'Kapazität', 'Fahrer', 'Partner']],
      body: vRows,
      theme: 'grid',
      headStyles: { fillColor: ACCENT, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9, cellPadding: 3 },
      styles: { fontSize: 9, cellPadding: 3, lineColor: BORDER, lineWidth: 0.3 },
      margin: { left: margin, right: margin },
      alternateRowStyles: { fillColor: LIGHT_BG }
    });
    y = (doc as any).lastAutoTable.finalY + 12;
  }

  // ===== STAFF ASSIGNMENTS =====
  const staff = planning.staffAssignments;
  if (staff && staff.length > 0) {
    checkPageBreak(30);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Betreuerstab', margin, y);
    y += 7;

    const roleLabels: Record<number, string> = {
      [StaffRole.Physio]: 'Physiotherapeut',
      [StaffRole.Arzt]: 'Arzt',
      [StaffRole.Athletik]: 'Athletiktrainer'
    };

    const sRows = staff.map(sa => [
      sa.traveler?.fullName || '-',
      roleLabels[sa.role] || String(sa.role),
      sa.notes || ''
    ]);

    autoTable(doc, {
      startY: y,
      head: [['Name', 'Funktion', 'Hinweis']],
      body: sRows,
      theme: 'grid',
      headStyles: { fillColor: ACCENT, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9, cellPadding: 3 },
      styles: { fontSize: 9, cellPadding: 3, lineColor: BORDER, lineWidth: 0.3 },
      margin: { left: margin, right: margin },
      alternateRowStyles: { fillColor: LIGHT_BG }
    });
    y = (doc as any).lastAutoTable.finalY + 12;
  }

  // ===== NOTES =====
  if (planning.notes) {
    checkPageBreak(25);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Hinweise', margin, y);
    y += 7;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const lines = doc.splitTextToSize(planning.notes, pageWidth - margin * 2);
    doc.text(lines, margin, y);
    y += lines.length * 5 + 10;
  }

  // ===== FOOTER on every page =====
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    // Footer line
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.5);
    doc.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15);
    // Footer text
    doc.setFontSize(7);
    doc.setTextColor(140, 140, 140);
    doc.text(
      `Erstellt: ${new Date().toLocaleDateString('de-DE')} ${new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`,
      margin, pageHeight - 10
    );
    doc.text('VoyagePlanner', pageWidth / 2, pageHeight - 10, { align: 'center' });
    doc.text(`Seite ${i}/${totalPages}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
  }

  // ===== SAVE =====
  const fileName = `Reiseplan_${planning.name.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
};

/**
 * Calculate gap in minutes between two consecutive segments
 */
const calculateGapMinutes = (current: PlanningSegment, next: PlanningSegment): number | null => {
  const toDate = (seg: PlanningSegment): Date | null => {
    const timeParts = seg.scheduledTime?.split(':');
    if (!timeParts || timeParts.length < 2) return null;
    const base = seg.scheduledDate ? new Date(seg.scheduledDate) : new Date();
    base.setHours(parseInt(timeParts[0]), parseInt(timeParts[1]), 0, 0);
    return base;
  };
  const d1 = toDate(current);
  const d2 = toDate(next);
  if (!d1 || !d2) return null;
  return Math.round((d2.getTime() - d1.getTime()) / 60000);
};
